import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  InputLabelProps,
  TextFieldProps,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PhoneInput, {
  DefaultInputComponentProps,
} from 'react-phone-number-input';
import { Countries, TP_COLORS } from '../../../../constants';

import {
  createUserAsAdmin,
  TCreateUserAsAdmin,
} from '../../../../store/UserSlice';

import { TPTextField } from '../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';
import UserRoleListDropDown, {
  TUserRoleSelectOption,
} from '../../../../components/Inputs/admin/UserRoleListDropDown';

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const phoneInputLabelProps: InputLabelProps = {
  shrink: true,
  sx: { color: TP_COLORS.black, backgroundColor: TP_COLORS.white },
};

const phoneInputProps: DefaultInputComponentProps = {
  international: true,
  className: 'phone-number-input-custom-styles',
  style: {
    height: '52px',
  },
  numberInputProps: {
    style: {
      height: '52px',
      borderColor: '#CBD5E1',
      fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.4375em',
    },
  },
};

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timeZone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

type TState = {
  name: string;
  email: string;
  phoneNumber: string;
  selectedUserRole: TUserRoleSelectOption | null;
};

const initialState: TState = {
  name: '',
  email: '',
  phoneNumber: '',
  selectedUserRole: null,
};

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
}

const AddUserModal: FunctionComponent<AddUserModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled =
    !state.name.trim().length ||
    !state.email.trim().length ||
    !state.phoneNumber.trim().length ||
    !state.selectedUserRole;

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (state.selectedUserRole) {
      const apiData: TCreateUserAsAdmin = {
        name: state.name,
        email: state.email,
        phoneNumber: state.phoneNumber,
        role: state.selectedUserRole.value,
        country: timeZone,
      };

      await dispatch(createUserAsAdmin(apiData));
    }

    onClose();
    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Add New User
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="name"
                  label="Name *"
                  placeholder="Name"
                  value={state.name}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="email"
                  label="Email Address *"
                  placeholder="Email Address"
                  value={state.email}
                  onChange={(event) =>
                    updateStateAttributes({ email: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel {...phoneInputLabelProps}>
                  Phone Number *
                </InputLabel>
                <PhoneInput
                  {...phoneInputProps}
                  defaultCountry={defaultCountryCode}
                  placeholder="Enter phone number"
                  value={state.phoneNumber}
                  onChange={(value) =>
                    updateStateAttributes({ phoneNumber: value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <UserRoleListDropDown
                selectedItem={state.selectedUserRole}
                handleChange={(e, value) =>
                  updateStateAttributes({
                    selectedUserRole: value,
                  })
                }
                componentProps={{
                  fullWidth: true,
                }}
                textFieldProps={{
                  label: 'Role *',
                  placeholder: 'Select',
                  ...textFieldProps,
                }}
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Add
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
